/**
 * MenuCustomizer — el usuario decide qué entradas del menú lateral quiere ver
 * en la empresa activa.
 *
 * Lo que oculta la empresa (política del superadmin) sale bloqueado: el usuario
 * puede ocultar más, nunca des-ocultar lo que la empresa ha ocultado.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  LockClosedIcon,
  StarIcon as StarIconOutline,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import AppModal from './AppModal';
import MenuVisibilityChecklist from './MenuVisibilityChecklist';
import {
  FAVORITES_LIMIT,
  MENU_CUSTOMIZER_GROUPS,
  PLATFORM_ADMIN_ITEM,
  buildFavoriteFromMenuItem,
} from '../menuConfig';
import './MenuCustomizer.css';

function MenuCustomizer({
  open,
  onClose,
  companyLabel,
  companyHidden = [],
  userHidden = [],
  menuMode = 'sections',
  favorites = { global: [], company: [] },
  initialTab = 'menu',
  isSuperadmin = false,
  saving = false,
  error = '',
  onSave,
  onSaveFavorites,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(initialTab === 'favorites' ? 'favorites' : 'menu');
  const [hidden, setHidden] = useState(() => new Set(userHidden));
  const [mode, setMode] = useState(menuMode);
  const [favScope, setFavScope] = useState('company');
  const [favDraft, setFavDraft] = useState(() => ({
    global: [...(favorites.global || [])],
    company: [...(favorites.company || [])],
  }));

  // Cada vez que se abre, partir de lo que hay guardado en el servidor.
  useEffect(() => {
    if (!open) return;
    setHidden(new Set(userHidden));
    setMode(menuMode);
    setTab(initialTab === 'favorites' ? 'favorites' : 'menu');
    setFavDraft({
      global: [...(favorites.global || [])],
      company: [...(favorites.company || [])],
    });
  }, [open, userHidden, menuMode, initialTab, favorites]);

  const locked = useMemo(() => new Set(companyHidden), [companyHidden]);

  const groups = useMemo(() => {
    if (!isSuperadmin) return MENU_CUSTOMIZER_GROUPS;
    return MENU_CUSTOMIZER_GROUPS.map((group) =>
      group.key === 'admin'
        ? { ...group, items: [...group.items, PLATFORM_ADMIN_ITEM] }
        : group
    );
  }, [isSuperadmin]);

  const allItems = useMemo(
    () => groups.reduce((acc, group) => acc.concat(group.items), []),
    [groups]
  );
  const visibleCount = allItems.filter(
    (item) => !hidden.has(item.key) && !locked.has(item.key)
  ).length;
  const lockedCount = allItems.filter((item) => locked.has(item.key)).length;

  const toggleItem = (key) => {
    if (locked.has(key)) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // showKeys = entradas que deben quedar visibles tras pulsar el grupo.
  const toggleGroup = (_group, showKeys) => {
    setHidden((prev) => {
      const next = new Set(prev);
      for (const key of showKeys) next.delete(key);
      return next;
    });
  };

  // ── Favoritos (borrador local hasta guardar) ─────────────────────────────
  const draftList = favDraft[favScope] || [];
  const draftKeys = useMemo(
    () => new Set((favDraft[favScope] || []).map((item) => item.menu_key)),
    [favDraft, favScope]
  );

  const toggleFavorite = (item) => {
    if (locked.has(item.key)) return;
    setFavDraft((prev) => {
      const list = [...(prev[favScope] || [])];
      const index = list.findIndex((row) => row.menu_key === item.key);
      if (index >= 0) {
        list.splice(index, 1);
      } else {
        if (list.length >= FAVORITES_LIMIT) return prev;
        const favorite = buildFavoriteFromMenuItem(item);
        if (favorite) list.push(favorite);
      }
      return { ...prev, [favScope]: list };
    });
  };

  const moveFavorite = (menuKey, direction) => {
    setFavDraft((prev) => {
      const list = [...(prev[favScope] || [])];
      const index = list.findIndex((row) => row.menu_key === menuKey);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= list.length) return prev;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...prev, [favScope]: list };
    });
  };

  const removeFavorite = (menuKey) => {
    setFavDraft((prev) => ({
      ...prev,
      [favScope]: (prev[favScope] || []).filter((row) => row.menu_key !== menuKey),
    }));
  };

  const labelOf = (item) => t(`menu.items.${item.key}`, item.label);

  const handleSubmit = () => {
    if (tab === 'favorites') {
      onSaveFavorites?.(favDraft);
      return;
    }
    onSave?.({ hiddenKeys: [...hidden], menuMode: mode });
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      size="lg"
      title={t('menu.customize.title', 'Personalizar menú')}
      subtitle={
        companyLabel
          ? t('menu.customize.subtitle', {
              defaultValue: 'Elige qué secciones quieres ver en {{company}}. Se guarda solo para ti.',
              company: companyLabel,
            })
          : t('menu.customize.subtitleNoCompany', 'Elige qué secciones quieres ver.')
      }
    >
      <div className="mcz-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'menu'}
          className={`mcz-tab ${tab === 'menu' ? 'is-active' : ''}`}
          onClick={() => setTab('menu')}
        >
          {t('menu.customize.tabMenu', 'Menú y visibilidad')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'favorites'}
          className={`mcz-tab ${tab === 'favorites' ? 'is-active' : ''}`}
          onClick={() => setTab('favorites')}
        >
          {t('menu.customize.tabFavorites', 'Favoritos')}
          <span className="mcz-tab-count">
            {(favDraft.company || []).length + (favDraft.global || []).length}
          </span>
        </button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {tab === 'menu' ? (
        <>
          <section className="mcz-mode">
            <h4 className="mcz-mode-heading">{t('menu.customize.modeTitle', 'Estilo del menú')}</h4>
            <div className="mcz-mode-options">
              <button
                type="button"
                className={`mcz-mode-card ${mode === 'sections' ? 'is-active' : ''}`}
                onClick={() => setMode('sections')}
                aria-pressed={mode === 'sections'}
              >
                <span className="mcz-mode-title">
                  {t('menu.customize.modeSections', 'Por secciones')}
                </span>
                <span className="mcz-mode-desc">
                  {t(
                    'menu.customize.modeSectionsDesc',
                    'Solo los títulos en el menú; al entrar en una sección se despliega su contenido, con tus favoritos arriba.'
                  )}
                </span>
                <span className="mcz-mode-badge">
                  {t('menu.customize.recommended', 'Recomendado')}
                </span>
              </button>
              <button
                type="button"
                className={`mcz-mode-card ${mode === 'classic' ? 'is-active' : ''}`}
                onClick={() => setMode('classic')}
                aria-pressed={mode === 'classic'}
              >
                <span className="mcz-mode-title">
                  {t('menu.customize.modeClassic', 'Clásico')}
                </span>
                <span className="mcz-mode-desc">
                  {t(
                    'menu.customize.modeClassicDesc',
                    'Todas las entradas a la vista, agrupadas en acordeones. Es el menú de siempre.'
                  )}
                </span>
              </button>
            </div>
          </section>

          <div className="mcz-hint">
            <span>
              {t('menu.customize.visibilityCount', {
                defaultValue: 'Se verán {{visible}} de {{total}} entradas del menú.',
                visible: visibleCount,
                total: allItems.length,
              })}
              {lockedCount > 0 && (
                <>
                  {' '}
                  {t('menu.customize.lockedCount', {
                    defaultValue:
                      'Hay {{count}} ocultas por la política de la empresa que no se pueden activar.',
                    count: lockedCount,
                  })}
                </>
              )}
            </span>
          </div>

          <MenuVisibilityChecklist
            groups={groups}
            hiddenKeys={hidden}
            lockedKeys={locked}
            onToggleItem={toggleItem}
            onToggleGroup={toggleGroup}
          />
        </>
      ) : (
        <div className="mcz-favs">
          <div className="mcz-scope" role="group">
            <button
              type="button"
              className={`mcz-scope-btn ${favScope === 'company' ? 'is-active' : ''}`}
              onClick={() => setFavScope('company')}
            >
              {companyLabel
                ? t('menu.customize.scopeCompanyNamed', {
                    defaultValue: 'Solo en {{company}}',
                    company: companyLabel,
                  })
                : t('menu.customize.scopeCompany', 'Solo en esta empresa')}
            </button>
            <button
              type="button"
              className={`mcz-scope-btn ${favScope === 'global' ? 'is-active' : ''}`}
              onClick={() => setFavScope('global')}
            >
              {t('menu.customize.scopeGlobal', 'En todas mis empresas')}
            </button>
          </div>

          <p className="mcz-favs-hint">
            {t(
              'menu.customize.favoritesHint',
              'Marca con la estrella las entradas que quieras tener siempre a mano. También puedes añadirlas desde la estrella de la barra superior, en cualquier pantalla.'
            )}
          </p>

          <div className="mcz-hint">
            <strong>
              {draftList.length}/{FAVORITES_LIMIT}
            </strong>{' '}
            {t('menu.customize.favoritesSelected', 'accesos directos en este ámbito.')}
          </div>

          {draftList.length > 0 && (
            <div className="mcz-selected">
              <div className="mcz-selected-title">
                {t('menu.customize.favoritesOrder', 'Orden en el menú')}
              </div>
              {draftList.map((favorite, index) => (
                <div className="mcz-selected-row" key={`sel-${favorite.menu_key}`}>
                  <StarIconSolid className="mcz-selected-star" />
                  <span className="mcz-selected-label">
                    {favorite.label || favorite.menu_key}
                  </span>
                  <button
                    type="button"
                    className="mcz-icon-btn"
                    onClick={() => moveFavorite(favorite.menu_key, -1)}
                    disabled={index === 0}
                    title={t('menu.customize.moveUp', 'Subir')}
                    aria-label={t('menu.customize.moveUp', 'Subir')}
                  >
                    <ArrowUpIcon />
                  </button>
                  <button
                    type="button"
                    className="mcz-icon-btn"
                    onClick={() => moveFavorite(favorite.menu_key, 1)}
                    disabled={index === draftList.length - 1}
                    title={t('menu.customize.moveDown', 'Bajar')}
                    aria-label={t('menu.customize.moveDown', 'Bajar')}
                  >
                    <ArrowDownIcon />
                  </button>
                  <button
                    type="button"
                    className="mcz-icon-btn is-danger"
                    onClick={() => removeFavorite(favorite.menu_key)}
                    title={t('menu.customize.removeFavorite', 'Quitar')}
                    aria-label={t('menu.customize.removeFavorite', 'Quitar')}
                  >
                    <XMarkIcon />
                  </button>
                </div>
              ))}
            </div>
          )}

          {groups.map((group) => (
            <section key={`fav-group-${group.key}`} className="mcz-fav-group">
              <header className="mcz-fav-group-head">
                <span className="mcz-fav-dot" style={{ background: group.color }} />
                <h4>{t(`menu.groups.${group.key}`, group.label)}</h4>
              </header>
              <div className="mcz-fav-items">
                {group.items.map((item) => {
                  const isLocked = locked.has(item.key);
                  const isFavorite = draftKeys.has(item.key);
                  return (
                    <button
                      key={`fav-${item.key}`}
                      type="button"
                      className={`mcz-fav-item ${isFavorite ? 'is-favorite' : ''} ${isLocked ? 'is-locked' : ''}`}
                      onClick={() => toggleFavorite(item)}
                      disabled={isLocked}
                      title={
                        isLocked
                          ? t('menu.customize.companyLocked', 'Oculto por la política de la empresa')
                          : labelOf(item)
                      }
                      aria-pressed={isFavorite}
                    >
                      {isFavorite ? (
                        <StarIconSolid className="mcz-fav-star" />
                      ) : (
                        <StarIconOutline className="mcz-fav-star" />
                      )}
                      <span>{labelOf(item)}</span>
                      {isLocked && <LockClosedIcon className="mcz-fav-lock" />}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <AppModal.Footer>
        {tab === 'menu' ? (
          <button
            type="button"
            className="quick-feedback-secondary"
            onClick={() => setHidden(new Set())}
            disabled={saving}
            title={t('menu.customize.showAllHint', 'Vuelve a mostrar todo lo que la empresa permita')}
          >
            {t('menu.customize.showAll', 'Mostrar todo')}
          </button>
        ) : (
          <button
            type="button"
            className="quick-feedback-secondary"
            onClick={() => setFavDraft((prev) => ({ ...prev, [favScope]: [] }))}
            disabled={saving || draftList.length === 0}
          >
            {t('menu.customize.clearFavorites', 'Vaciar este ámbito')}
          </button>
        )}
        <button type="button" className="quick-feedback-secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel', 'Cancelar')}
        </button>
        <button
          type="button"
          className="quick-feedback-primary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? t('common.saving', 'Guardando...') : t('common.save', 'Guardar')}
        </button>
      </AppModal.Footer>
    </AppModal>
  );
}

export default MenuCustomizer;
